import './LanderConfirmationPage.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from '@reach/router';
import { Checkbox, Tag } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import Button from '../../../../components/Button/Button';
import { recaptcha } from '../../../../config/externalServices';
import { useConfirmLandingStep } from '../../../../core/APIHooks/useConfirmLandingStep';

const validationSchema = Yup.object().shape({
  terms_and_conditions: Yup.boolean()
    .required('The terms and conditions must be accepted.')
    .oneOf([true], 'The terms and conditions must be accepted.')
});

const RECAPTCHA_SITE_KEY = recaptcha.siteKey;

export default function LanderConfirmationPage() {
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('userToken');
  const tokenData = token && JSON.parse(window.atob(token));
  const { mutate: confirmLogin, isLoading } = useConfirmLandingStep();
  const navigate = useNavigate();
  const [captcha, setCaptcha] = useState();
  const recaptchaRef = useRef(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  });

  const termsAccepted = watch('terms_and_conditions');

  useEffect(() => {
    localStorage.setItem('current-email', tokenData?.email);
  }, [tokenData]);

  useEffect(() => {
    let script = null;

    const initializeRecaptcha = () => {
      if (tokenData?.recaptcha && recaptchaRef.current) {
        window.grecaptcha.enterprise.ready(() => {
          // Reset any existing widget
          if (recaptchaWidgetId !== null) {
            window.grecaptcha.enterprise.reset(recaptchaWidgetId);
          }

          // Render new widget
          const widgetId = window.grecaptcha.enterprise.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            theme: 'dark',
            callback: (token) => {
              setCaptcha(token);
              setIsSubmitting(false);
            },
            'expired-callback': () => {
              setCaptcha(null);
              setIsSubmitting(false);
            },
            size: 'normal'
          });
          setRecaptchaWidgetId(widgetId);
        });
      }
    };

    // Load reCAPTCHA Enterprise script
    if (tokenData?.recaptcha) {
      script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=explicit`;
      script.async = true;
      script.defer = true;
      script.onload = initializeRecaptcha;
      document.head.appendChild(script);
    }

    return () => {
      if (script) {
        document.head.removeChild(script);
      }
      if (recaptchaWidgetId !== null) {
        window.grecaptcha?.enterprise?.reset(recaptchaWidgetId);
      }
    };
  }, [tokenData?.recaptcha]);

  const submitForm = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (tokenData?.recaptcha && !captcha) {
      return;
    }

    if (!termsAccepted) {
      return;
    }

    setIsSubmitting(true);
    handleSubmit((data) => onSubmit(data))();
  };

  console.log(tokenData);

  const onSubmit = async (val) => {
    if (!val.terms_and_conditions) {
      setIsSubmitting(false);
      return;
    }

    if (tokenData?.recaptcha && !captcha) {
      setIsSubmitting(false);
      return;
    }

    const confirmData = {
      token: token,
      terms_and_conditions: val.terms_and_conditions,
      ...(tokenData?.recaptcha ? { recaptcha_token: captcha } : {})
    };

    confirmLogin(
      { confirmData },
      {
        onSuccess: (res) => {
          navigate(res.redirect_url);
        },
        onError: () => {
          setIsSubmitting(false);
          if (tokenData?.recaptcha) {
            setCaptcha(null);
            if (recaptchaWidgetId !== null) {
              window.grecaptcha?.enterprise?.reset(recaptchaWidgetId);
            }
          }
        }
      }
    );
  };

  return (
    <div className="LanderConfirmationContainer">
      <div className="ConfirmationBackgroundOverlay"></div>

      <div className="LanderConfirmationContent">
        <div className="LanderContentInner">
          <div className="InnerHeader">
            <span>Sign up now for FREE!</span>
            <span>No credit card required</span>
          </div>
          <div className="InnerContainer">
            <div className="ConfirmationContainer">
              <form>
                <div className="StepSubTitle">
                  Please check your details and confirm your profile
                </div>
                <div className="CheckInfoContainer">
                  <p>
                    <Checkbox checked={true} />{' '}
                    <span className="CheckboxLabel">
                      <b>E-mail:</b> {tokenData?.email}
                    </span>
                  </p>
                  <p>
                    <Checkbox checked={true} />{' '}
                    <span className="CheckboxLabel">
                      <b>Username:</b> {tokenData?.username}
                    </span>
                  </p>
                  <p>
                    <Controller
                      name="terms_and_conditions"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <span className="TermsLabel">
                      By becoming a member I agree to the{' '}
                      <a href="/terms-and-conditions">Terms & Conditions</a> &{' '}
                      <a href="/privacy-policy">Privacy Policy</a>
                    </span>
                  </p>

                  {errors.terms_and_conditions && (
                    <Tag color="red">{errors.terms_and_conditions.message}</Tag>
                  )}
                </div>
                {tokenData?.recaptcha && (
                  <div className="recaptcha">
                    <div
                      ref={recaptchaRef}
                      className="g-recaptcha"
                      data-sitekey={RECAPTCHA_SITE_KEY}
                      data-theme="dark"
                    ></div>
                  </div>
                )}
                <div className="ButtonsContianer">
                  <Button
                    onClick={submitForm}
                    className="ConfirmationButton"
                    text="CONFIRM"
                    type="button"
                    loading={isLoading || isSubmitting}
                    disabled={
                      isLoading ||
                      isSubmitting ||
                      !termsAccepted ||
                      (tokenData?.recaptcha && !captcha)
                    }
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
